import { Button } from "@2u-landing-pages/components/Button";
import { ContentfulImage } from "@2u-landing-pages/components/ContentfulImage";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { ItemsContainer } from "@2u-landing-pages/components/ItemsContainer";
import { Banner } from "@2u-landing-pages/templates/Banner";
import {
  ContentfulImageConfig,
  ContentfulImageParams,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeButtonWithoutUnresolvableLinksResponse,
  TypeContent_liteWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useParseMeta } from "@2u-landing-pages/providers";
import { renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { Body, ButtonGroup, Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { useMemo } from "react";
import { CustomCSSProperties, DetailCalloutProps } from "../../DetailCallout.types";
// @ts-ignore
import styles from "./Default.module.scss";

const spacingMap: Record<string, string> = {
  "Large (24/16)": "large",
  "Medium (16/12)": "medium",
  "Small (8/4)": "small",
  "None (0/0)": "none",
};

export const Default_v2: React.FC<DetailCalloutProps> = ({
  id,
  background_color,
  className,
  content,
  header_image,
  image_config,
  desktop_orientation = "stacked",
  mobile_orientation = "stacked",
  stacked_layout_padding = "Large (24/16)",
  mobile_reverse_order = false,
  footnote,
  footnote_background_color,
  footnote_text_color,
  phoneNumber,
  ctaLocation = "Detail Callout",
}) => {
  const style: CustomCSSProperties = {};
  const inspector = useContentfulInspectorMode();
  const parseMetadata = useParseMeta();
  const imageConfig = image_config as unknown as {
    default: ContentfulImageParams;
    mobile?: ContentfulImageParams;
  };

  if (background_color) style["--bg-color"] = `var(${background_color})`;
  if (footnote_background_color) style["--footnote-bg-color"] = `var(${footnote_background_color})`;
  if (footnote_text_color) style["--footnote-color"] = `var(${footnote_text_color})`;

  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;
  const isSvgImage = header_image?.fields?.file?.contentType === "image/svg+xml";

  // Merge gradient overlay styles
  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};
  Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

  const renderFootnote = useMemo(() => {
    return footnote ? renderText(parseMetadata ? parseMetadata(footnote) : footnote, phoneNumber) : "";
  }, [footnote, parseMetadata, phoneNumber]);

  const hasButton = useMemo(() => {
    return content?.some((section) => section?.sys?.contentType?.sys?.id === "button") ?? false;
  }, [content]);

  return (
    <div
      className={cn(className, styles.detailCallout, {
        [styles[`spacing-mobile-${spacingMap[stacked_layout_padding]}`]]: mobile_orientation === "stacked",
        [styles[`spacing-desktop-${spacingMap[stacked_layout_padding]}`]]: desktop_orientation === "stacked",
      })}
      style={style}
    >
      {header_image?.fields?.file?.url && (
        <div
          className={cn(styles.featuredImage, {
            [styles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
            [styles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
          })}
        >
          <ContentfulImage
            image={header_image}
            config={image_config as ContentfulImageConfig | undefined}
            className={cn({ [styles.svgImage]: isSvgImage })}
            {...(id ? inspector({ entryId: id, fieldId: "header_image" }) : {})}
          />
        </div>
      )}
      <Container
        className={cn(styles.content, {
          [styles.desktopStacked]: desktop_orientation === "stacked",
          [styles.desktopInline]: desktop_orientation === "inline",
          [styles.mobileStacked]: mobile_orientation === "stacked",
          [styles.mobileInline]: mobile_orientation === "inline",
          [styles.mobileReverseOrder]: mobile_reverse_order && mobile_orientation === "stacked",
          [styles.hasButton]: hasButton,
        })}
      >
        {content?.map((section, index) => {
          if (!section) return null;

          if (!section?.sys?.contentType?.sys?.id) return null;

          if (section?.sys?.contentType?.sys?.id === "content_lite") {
            const content = section as TypeContent_liteWithoutUnresolvableLinksResponse;
            return (
              <ContentLayout
                key={index}
                className={styles.heading}
                content={content}
                heading={{
                  variant: "heading-xl",
                }}
                phoneNumber={phoneNumber}
              />
            );
          }

          if (section?.sys?.contentType?.sys?.id === "button") {
            const button = section as TypeButtonWithoutUnresolvableLinksResponse;
            return (
              <ButtonGroup
                placement="center"
                key={section?.sys.id}
                className={cn(styles.button, {
                  [styles.desktopInline]: desktop_orientation === "inline",
                  [styles.mobileInline]: mobile_orientation === "inline",
                })}
              >
                <Button
                  location={ctaLocation}
                  {...button?.fields}
                  phoneNumber={phoneNumber}
                >
                  {button?.fields.label}
                </Button>
              </ButtonGroup>
            );
          }

          if (section?.sys?.contentType?.sys?.id === "items_container") {
            const container = section as TypeItems_containerWithoutUnresolvableLinksResponse;
            return (
              <ItemsContainer
                key={index}
                entry={container}
                orientation="horizontal"
                heading={{ variant: "heading-lg" }}
                body={{ variant: "body-sm" }}
                phoneNumber={phoneNumber}
              />
            );
          }

          if (section?.sys?.contentType?.sys?.id === "banner") {
            const banner = section as TypeBannerWithoutUnresolvableLinksResponse;
            const bannerStyle: React.CSSProperties = {
              flexDirection: "column",
              borderRadius: "8px",
            };
            return (
              <Banner
                key={index}
                inspector={inspector}
                {...banner?.fields}
                className={"detailCalloutBanner"}
                style={bannerStyle}
              />
            );
          }
        })}
      </Container>
      {footnote && (
        <Body
          variant="body-sm"
          className={cn(styles.footnote, {
            [styles.noBackground]: !footnote_background_color,
          })}
        >
          {renderFootnote}
        </Body>
      )}
    </div>
  );
};
